import { useState } from "react";
import {
  Box,
  Button as MuiButton,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  Stack,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import SaveIcon from "@mui/icons-material/Save";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import { Button, Form, useNotify, useRefresh } from "react-admin";
import dataProvider from "server/dataProvider";
import { doctoralRequests } from "api/doctoralApi";
import permissions from "app/permissions";
import SecuredComponent from "server/security/SecuredComponent";
import EmployeeInput from "server/components/Fields/EmployeeInput";
import { employeeRequests } from "api/employeeApi";
import { useNotifyError } from "utils/useNotifyError";
import ModalTitle, {
  withModalSx,
  modalButtonSx,
  modalCancelButtonSx,
  modalConfirmButtonSx,
  modernUiProps,
} from "server/components/Modals/ModalTitle";

const ExamCommitteeDialog = ({ campaignId, disciplineId, structuralUnitId, exam }) => {
  const notify = useNotify();
  const refresh = useRefresh();
  const notifyError = useNotifyError();

  const [open, setOpen] = useState(false);
  const [saving, setSaving] = useState(false);

  const defaultValues = {
    chairEmployeeId: exam?.chairEmployeeId ?? null,
    member1EmployeeId: exam?.member1EmployeeId ?? null,
    member2EmployeeId: exam?.member2EmployeeId ?? null,
  };

  const handleSave = async data => {
    if (saving) return;
    setSaving(true);
    const payload = {
      chairEmployeeId: data.chairEmployeeId ?? null,
      member1EmployeeId: data.member1EmployeeId ?? null,
      member2EmployeeId: data.member2EmployeeId ?? null,
    };
    try {
      await dataProvider.customRequest(doctoralRequests.setExamCommittee(campaignId, disciplineId), payload, "PUT");
      notify("Изпитната комисия е запазена", { type: "success" });
      refresh();
      setOpen(false);
    } catch (err) {
      notifyError(err);
    } finally {
      setSaving(false);
    }
  };

  const handleApprove = async () => {
    if (saving) return;
    setSaving(true);
    try {
      await dataProvider.customRequest(doctoralRequests.approveExamCommittee(campaignId, disciplineId), null, "POST");
      notify("Комисията е утвърдена", { type: "success" });
      refresh();
      setOpen(false);
    } catch (err) {
      notifyError(err);
    } finally {
      setSaving(false);
    }
  };

  return (
    <>
      <MuiButton
        size="small"
        variant="outlined"
        onClick={() => setOpen(true)}
        sx={{ whiteSpace: "nowrap", minWidth: "auto" }}
      >
        Изпитна комисия
      </MuiButton>

      <Dialog
        open={open}
        onClose={() => !saving && setOpen(false)}
        fullWidth
        maxWidth="md"
        sx={withModalSx()}
      >
        <ModalTitle title="Изпитна комисия" />
        <Form
          record={defaultValues}
          onSubmit={handleSave}
        >
          <DialogContent sx={{ px: 4 }}>
            {exam?.hasCommittee && (
              <Box sx={{ mb: 1 }}>
                <Chip
                  size="small"
                  color={exam.isCommitteeApproved ? "success" : "default"}
                  label={exam.isCommitteeApproved ? "Утвърдена от Декан" : "Очаква утвърждаване"}
                />
              </Box>
            )}
            <Stack
              spacing={2}
              sx={{ mt: 1 }}
            >
              <EmployeeInput
                source="chairEmployeeId"
                label="Председател (от факултета)"
                fullWidth
                filters={{ structuralUnitId }}
                reference={employeeRequests.getEmployeesWithDegreeSelect()}
              />
              <EmployeeInput
                source="member1EmployeeId"
                label="Член 1 (от факултета)"
                fullWidth
                filters={{ structuralUnitId }}
                reference={employeeRequests.getEmployeesWithDegreeSelect()}
              />
              <EmployeeInput
                source="member2EmployeeId"
                label="Член 2 (от факултета)"
                fullWidth
                filters={{ structuralUnitId }}
                reference={employeeRequests.getEmployeesWithDegreeSelect()}
              />
            </Stack>
          </DialogContent>
          <DialogActions sx={{ display: "flex", justifyContent: "space-between", paddingX: 3, paddingBottom: 2 }}>
            <Button
              size="medium"
              variant="outlined"
              {...modernUiProps({ color: "error" })}
              sx={modalCancelButtonSx}
              startIcon={<CloseIcon />}
              label="Отказ"
              onClick={() => !saving && setOpen(false)}
              disabled={saving}
            />
            <Box sx={{ display: "flex", gap: 1 }}>
              {exam?.hasCommittee && !exam?.isCommitteeApproved && (
                <SecuredComponent permission={permissions.canApproveDoctoralExamCommittees}>
                  <Button
                    size="medium"
                    variant="contained"
                    color="success"
                    sx={modalButtonSx}
                    startIcon={<CheckCircleIcon />}
                    label="Утвърди"
                    onClick={handleApprove}
                    disabled={saving}
                  />
                </SecuredComponent>
              )}
              <Button
                type="submit"
                size="medium"
                variant="contained"
                sx={modalConfirmButtonSx}
                startIcon={<SaveIcon />}
                label="Запази"
                disabled={saving}
              />
            </Box>
          </DialogActions>
        </Form>
      </Dialog>
    </>
  );
};

export default ExamCommitteeDialog;
